import { FC, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import { useTranslate } from 'hooks/useTranslate';

import { Partner } from 'types';
import { TradingOfferType } from 'types/TradingOffer';

import { TradingOfferOperationStatus } from 'types/TradingOfferOperation';
import { useTradingOfferOperationDetail } from '../context/TradingOfferOperationDetailContext';
import getOfferReference from '../../../utils/getOfferReference';

const PLACEHOLDER = '-';

type PaymentDataRowProps = {
  label: string;
  value: string;
  copyLabel: string;
  hasBorderBottom?: boolean;
};

const PaymentDataRow: FC<PaymentDataRowProps> = ({
  label,
  value,
  copyLabel,
  hasBorderBottom = true,
}) => {
  const { t } = useTranslate();

  const canCopy = value !== PLACEHOLDER;

  const handleCopy = useCallback(async () => {
    if (!canCopy) return;

    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(value);
      Swal.fire({
        toast: true,
        position: 'top-end',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        icon: 'success',
        title: t('CopiedToClipboard'),
      });
    } catch {
      Swal.fire({
        toast: true,
        position: 'top-end',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        icon: 'error',
        title: t('CopyFailed'),
      });
    }
  }, [value, canCopy, t]);

  return (
    <div className="col-12">
      <div
        className={`d-flex justify-content-between align-items-center py-1 ${
          hasBorderBottom ? 'border-bottom' : ''
        }`}
      >
        <span className="text-soft">{label}</span>
        <div className="d-inline-flex align-items-center">
          <strong>{value}</strong>
          <button
            type="button"
            className="btn btn-trigger btn-icon btn-sm text-primary"
            title={copyLabel}
            aria-label={copyLabel}
            onClick={handleCopy}
            disabled={!canCopy}
          >
            <em className="icon ni ni-copy" />
          </button>
        </div>
      </div>
    </div>
  );
};

const TradingOfferOperationDetailPaymentCard: FC = () => {
  const { t } = useTranslate();
  const { data } = useTradingOfferOperationDetail();

  // Payment is always made to the seller, regardless of offer direction.
  const seller = useMemo((): Partial<Partner> | null => {
    if (!data.operation) return null;

    const party = data.operation.seller;

    if (!party || typeof party === 'string') return null;
    return party as Partial<Partner>;
  }, [data.operation]);

  const offerReference = useMemo(
    () => getOfferReference(data.tradingOffer),
    [data.tradingOffer]
  );

  const operationType = useMemo(() => {
    if (!data.tradingOffer) return '';
    return data.tradingOffer.type === TradingOfferType.BUY
      ? t('Buy').toLowerCase()
      : t('Sale').toLowerCase();
  }, [data.tradingOffer, t]);

  const paymentConcept = useMemo(
    () => `${t('Operation')} ${operationType} ${offerReference}`,
    [t, operationType, offerReference]
  );

  const accountNumber = seller?.bankAccountNumber || PLACEHOLDER;
  const sellerName = seller?.name || PLACEHOLDER;

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner py-3">
        <h6 className="card-title mb-2 d-flex align-items-center justify-content-between">
          <span>
            <em className="icon ni ni-cc-alt2 text-primary mr-2" />
            {t('PaymentData')}
          </span>
          <span className="badge badge-dim badge-warning">
            <em className="icon ni ni-alert-circle mr-1" />
            {t('Pending')}
          </span>
        </h6>

        {data.operationStatus ===
          TradingOfferOperationStatus.PAYMENT_PENDING && (
          <div className="alert alert-warning py-2 px-3 mb-2">
            <div className="d-flex align-items-center fs-12px">
              <div
                className="spinner-border spinner-border-sm text-warning mr-2"
                role="status"
                aria-hidden="true"
              />
              <span>
                <strong>{t('PendingPayment')}:</strong>{' '}
                {t('PendingPaymentDescription')}
              </span>
            </div>
          </div>
        )}

        <div className="row fs-13px">
          <PaymentDataRow
            label={t('SellerAccountNumber')}
            value={accountNumber}
            copyLabel={t('CopyAccountNumber')}
          />
          <PaymentDataRow
            label={t('SellerName')}
            value={sellerName}
            copyLabel={t('CopySellerName')}
          />
          <PaymentDataRow
            label={t('Concept')}
            value={paymentConcept}
            copyLabel={t('CopyConcept')}
            hasBorderBottom={false}
          />
        </div>
      </div>
    </div>
  );
};

export default TradingOfferOperationDetailPaymentCard;
